import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { debounce, isEmpty, startCase } from 'lodash';
import { formatNumber } from '../../../../../core/utils/campaigns';

// =============================================================================
// BreakdownRingChart — shared donut for "share of X across entities" surfaces.
//
// Replaces per-feature BizCharts donuts (CreativeRingChart, PublisherRingChart,
// and future ones). Renders every record as its own slice by default —
// stakeholders want full visibility, not a rolled-up "Other" bucket.
// Optional Top-N + "Other (N)" aggregation is available as an opt-in via
// `maxSlices` for surfaces that need the long-tail rollup.
//
// Props:
//   data             Array<{ value, color, impressions, clicks, percentctr, ... }>
//   dimension        'impressions' | 'clicks' | 'CTR'
//   height           Max-width cap for the chart (number, px). The chart is
//                    always square (width === height via aspect:1). Default
//                    480 — comfortably fills typical breakdown sidebars
//                    (`.campaign-overview__creatives-breakdown` @ flex 3/10
//                    of a 75% column on desktop). Drop lower only when the
//                    consumer's container is intentionally narrow.
//   maxSlices        Number of real slices before rolling the rest into "Other"
//                    (default Infinity — every record renders as its own slice).
//                    Pass a finite number to opt into long-tail aggregation.
//   otherLabel       Label for the aggregated bucket (default 'Other')
//   otherColor       Fill for the aggregated bucket (default neutral grey)
// =============================================================================

// Common dimension labels. Consumers with a novel dimension key can pass
// `dimensionLabels` to override; falls back to startCase(dimension) for
// anything not listed here.
const DEFAULT_DIMENSION_LABELS = {
  impressions: 'Impressions',
  clicks: 'Clicks',
  CTR: 'CTR',
  transactions: 'Transactions',
  totalTransactions: 'Transactions',
  revenue: 'Revenue',
  customers: 'Customers',
  spend: 'Spend',
  ais: 'Ad Impressions',
};

const DEFAULT_OTHER_COLOR = '#cbd5e1';

const BreakdownRingChart = ({
  data,
  dimension = 'impressions',
  height = 480,
  maxSlices = Infinity,
  otherLabel = 'Other',
  otherColor = DEFAULT_OTHER_COLOR,
  dimensionLabels,
}) => {
  const labels = { ...DEFAULT_DIMENSION_LABELS, ...(dimensionLabels || {}) };
  const dimensionLabel = labels[dimension] || startCase(dimension);

  // Window-level resize listener that bumps a remount key. Same pattern as
  // DualAxisAreaChart — Recharts' ResponsiveContainer ResizeObserver fails
  // inside flex-column parents because the inner SVG's intrinsic size keeps
  // the wrapper from reporting a width change. Re-mounting on resize forces
  // a fresh measurement.
  const [resizeKey, setResizeKey] = useState(0);
  const handleResize = useCallback(
    debounce(() => setResizeKey((k) => k + 1), 150),
    []
  );
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  // Normalize each row's slice value against the active dimension, filter
  // zero-value slices, sort desc so "Other" picks up the long tail (not the
  // biggest category by accident).
  //
  // Dimension resolution:
  //   'CTR' — special-cases percentctr (stored as a 0–1 fraction so tooltip
  //           math works consistently; formatSliceValue multiplies by 100)
  //   anything else — reads `item[dimension]` directly. Consumers whose
  //   data shape stores the dimension under a different key should map
  //   before passing (see SegmentsRingChart for an ais → impressions
  //   example).
  const normalizedRows = useMemo(() => {
    if (isEmpty(data)) return [];
    return data
      .map((item) => {
        const slice = dimension === 'CTR'
          ? Number(item.percentctr) || 0
          : Number(item[dimension]) || 0;
        return {
          ...item,
          name: item.value || item.name || 'Unnamed',
          color: item.color || '#cbd5e1',
          slice,
        };
      })
      .filter((r) => r.slice > 0)
      .sort((a, b) => b.slice - a.slice);
  }, [data, dimension]);

  // Top-N + Other aggregation. If the long tail has 2+ rows, we roll them.
  // A single-row "Other" with just 1 member is silly — keep it as itself.
  const rows = useMemo(() => {
    if (normalizedRows.length <= maxSlices) return normalizedRows;
    const head = normalizedRows.slice(0, maxSlices - 1);
    const tail = normalizedRows.slice(maxSlices - 1);
    if (tail.length <= 1) return normalizedRows;
    const tailSlice = tail.reduce((sum, r) => sum + r.slice, 0);
    return [
      ...head,
      {
        name: `${otherLabel} (${tail.length})`,
        color: otherColor,
        slice: tailSlice,
        __isOther: true,
        __aggregatedCount: tail.length,
      },
    ];
  }, [normalizedRows, maxSlices, otherLabel, otherColor]);

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + r.slice, 0),
    [rows]
  );

  if (isEmpty(rows)) {
    return (
      <div
        style={{
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted, #94a3b8)',
          fontSize: 12,
        }}
      >
        No data to breakdown yet.
      </div>
    );
  }

  const formatSliceValue = (val) => {
    if (dimension === 'CTR') {
      return `${formatNumber(Number(val) * 100)}%`;
    }
    return Number(val).toLocaleString();
  };

  // Center label: total of the active dimension, shown in the donut hole.
  // For CTR, summing per-row ratios is meaningless — compute blended
  // sum(clicks)/sum(impressions) across the source data and render as %.
  const centerTotal = (() => {
    if (dimension === 'CTR') {
      const totalImpressions = data.reduce((s, r) => s + (Number(r.impressions) || 0), 0);
      const totalClicks = data.reduce((s, r) => s + (Number(r.clicks) || 0), 0);
      const blended = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
      return formatSliceValue(blended);
    }
    return formatSliceValue(total);
  })();

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0];
    const row = entry.payload;
    const share = total > 0 ? ((row.slice / total) * 100).toFixed(1) : '0.0';
    const isOther = !!row.__isOther;
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #eeeeee',
          borderRadius: 12,
          boxShadow:
            '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)',
          fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
          fontSize: 13,
          padding: 12,
          minWidth: 180,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 9999,
              background: row.color,
              flexShrink: 0,
            }}
          />
          <strong
            style={{
              color: '#111827',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.name}
          </strong>
        </div>
        <div style={{ color: '#374151', fontSize: 12, lineHeight: 1.5 }}>
          {dimensionLabel}: <strong>{formatSliceValue(row.slice)}</strong>
          <br />
          Share: <strong>{share}%</strong>
          {isOther && (
            <>
              <br />
              <span style={{ color: '#6b7280', fontSize: 11 }}>
                Combined across {row.__aggregatedCount} items
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      key={resizeKey}
      className="ds-ring-chart"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: height,
        minWidth: 0,
        margin: '0 auto',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#94a3b8',
            marginBottom: 4,
          }}
        >
          {dimensionLabel}
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: '#111827',
            letterSpacing: '-0.01em',
          }}
        >
          {centerTotal}
        </div>
      </div>
      <ResponsiveContainer width="100%" aspect={1}>
        <PieChart>
          <Pie
            data={rows}
            dataKey="slice"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="86%"
            paddingAngle={rows.length > 8 ? 0 : (rows.length > 1 ? 1 : 0)}
            stroke="#fff"
            strokeWidth={rows.length > 8 ? 0.5 : 2}
            animationDuration={700}
            animationEasing="ease-out"
            isAnimationActive
          >
            {rows.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={renderTooltip} cursor={false} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BreakdownRingChart;
